/**
 * WCS (Welding Consumable Specification) Service
 * Handles all API calls related to welding consumable specification management
 * Used by: WCS management components, grid views, project management, and validation modules
 * 
 * Main Functions:
 * - WCS header and detail management
 * - Grid data retrieval and filtering
 * - Project-based WCS operations
 * - Excel data upload and validation
 * - Joint number and WPS validation
 */

import axios from "axios";

// API URL configuration based on environment
let WCS_API_URL;

if (process.env.REACT_APP_NODE_ENV === "local") {
  WCS_API_URL = process.env.REACT_APP_WCS_API_URL;
} else {
  WCS_API_URL = process.env.REACT_APP_API_URL + "wcs";
}

// ==================== WCS DATA RETRIEVAL FUNCTIONS ====================

/**
 * Get all WCS records by type
 * Called from: WCS listing pages, type-specific views, dashboard components
 * @param {string} type - WCS type filter
 * Returns: Array of WCS records filtered by type
 */
export async function getAllWCS(type) {
  const response = await axios
    .get(WCS_API_URL + `/WcsHeaderMast/${type}`)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Get WCS header data for grid display
 * Called from: WCS management grids, dashboard tables, header listing components
 * @param {string} band - User band filter (unused in current implementation)
 * @param {string} user - User filter (unused in current implementation)
 * Returns: WCS header data optimized for grid display
 */
export async function getGridHeaderData(band, user) {
  const response = await axios.get(WCS_API_URL + `/WcsHeaderMast/grid`)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}

/**
 * Get WCS detail data for specific project
 * Called from: WCS detail grids, project-specific detail views
 * @param {string} WCS_Project_No - Project number
 * @param {string} user - User identifier
 * Returns: WCS detail records for the specified project
 */
export async function getGridDetailData(WCS_Project_No, user) {
  let reqData = { WCS_Project_No: WCS_Project_No, user: user };
  const response = await axios.post(WCS_API_URL + `/WcsHeaderMast/detailGrid`, reqData)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
}


export const checkProjectWCS = async (body) => {
  let url = `${WCS_API_URL}/WcsHeaderMast/check-project-wcs`;
  const response = await axios.post(url, body)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Get all WPS numbers for dropdown/selection
 * Called from: WPS selection dropdowns, form components, validation modules
 * Returns: Array of available WPS numbers
 * NOTE: This function may be unused - check frontend usage
 */
/* export async function getAllWPSNo() {
  const response = await axios.get(WCS_API_URL + "/WcsHeaderMast/allwpsno")
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
} */

/**
 * Get WCS data by project number
 * Called from: Project-specific WCS lookups, project management modules
 * @param {string} IdprojectPK - Project primary key
 * @param {string} IdprojectSK - Project sort key (unused in current implementation)
 * Returns: WCS records for the specified project
 */
export const getbyprojectno = async (IdprojectPK, IdprojectSK) => {
  let url = `${WCS_API_URL}/WcsHeaderMast/getprojectnowcs`;
  let reqData = { PK: IdprojectPK };

  const response = await axios.post(url, reqData)
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
};

// ==================== WCS CRUD OPERATIONS ====================

/**
 * Add new WCS record
 * Called from: WCS creation forms, add new WCS dialogs
 * @param {Object} WCS_Req - WCS request data object
 * Returns: Creation confirmation and WCS details
 */
export const AddNewWCS = async (WCS_Req) => {
  let url = `${WCS_API_URL}/WcsHeaderMast`;
  const response = await axios.post(url, WCS_Req)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
};

/**
 * Add new count/counter record
 * Called from: Counter management, sequence generation
 * @param {Object} WPS_Header_Master - Counter data object
 * Returns: Counter creation confirmation
 * NOTE: Parameter name suggests WPS but used for WCS counter
 */
export const AddNewCount = async (WPS_Header_Master) => {
  let url = `${WCS_API_URL}/WcsHeaderMast`;
  const response = await axios.post(url, WPS_Header_Master)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Update existing WCS record
 * Called from: WCS edit forms, WCS modification dialogs
 * @param {Object} WCS_Header_Master - Updated WCS data with SK identifier
 * Returns: Update confirmation
 */
export const UpdateWCS = async (WCS_Header_Master) => {
  let url = `${WCS_API_URL}/WcsHeaderMast/update/${WCS_Header_Master.SK}`;
  const response = await axios.post(url, WCS_Header_Master)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Delete WCS detail record
 * Called from: WCS detail grids (delete actions), detail management components
 * @param {Object} gridDetail - Detail record to be deleted
 * Returns: Deletion confirmation
 */
export async function DeleteWCSDetail(gridDetail) {
  let url = `${WCS_API_URL}/WcsHeaderMast/deleteDetail`;
  const resp = await axios.post(url, gridDetail)
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return error.message;
    });
  return resp;
}

/**
 * Update WCS status
 * Called from: Status change operations, workflow management, approval processes
 * @param {Object} WCS_Header_Master - WCS data with updated status
 * Returns: Status update confirmation
 */
export const UpdateWCSStatus = async (WCS_Header_Master) => {
  let url = `${WCS_API_URL}/WcsHeaderMast/updateStatus`;
  const response = await axios.post(url, WCS_Header_Master)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

/**
 * Get WCS data for Work Instructions (WI)
 * Called from: Work Instruction generation modules, WI creation workflows
 * @param {Object} wcsheader - WCS header identifier
 * Returns: WCS data formatted for work instruction use
 */
export async function getWCSDataHeader(wcsheader) {
  let url = `${WCS_API_URL}/WcsHeaderMast/getwcsforwi`;
  const resp = await axios.post(url, wcsheader)
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return error.message;
    });
  return resp;
}

/**
 * Update WCS detail record
 * Called from: WCS detail edit forms, detail modification operations
 * @param {Object} WCS_Detail_Master - Updated WCS detail data
 * Returns: Detail update confirmation
 */
export const UpdateDetailWCS = async (WCS_Detail_Master) => {
  let url = `${WCS_API_URL}/WcsHeaderMast/updateDetail`;
  const response = await axios.post(url, WCS_Detail_Master)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  return response.data;
};

// ==================== EXCEL UPLOAD AND VALIDATION FUNCTIONS ====================

/**
 * Upload WCS Excel data
 * Added by: Amol Pawar on 25/10/23
 * Called from: Excel upload components, bulk data import features
 * @param {Object} data - Excel file data to be uploaded
 * Returns: Upload confirmation and processing results
 */
export const uploadWCSexceldata = async (data) => {
  let url = `${WCS_API_URL}/WcsExcelDataUpload/uploadwcsexceldata`;
  const response = await axios.post(url, data)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });

  return response.data;
};

/**
 * Validate and check joint number
 * Called from: Joint number validation, WCS form validation, data entry components
 * @param {Object} reqData - Joint number validation request data
 * Returns: Array of found/valid joint numbers
 */
export const checkValidJointNo = async (reqData) => {
  let url = `${WCS_API_URL}/WcsHeaderMast/getjointno`;
  const response = await axios.post(url, reqData)
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      return { message: "Unable to find request", status: false };
    });
  let resArray = response.data["foundJointNo"] || response.data["abc"]
  return resArray;
};

/**
 * Check if WPS number is present/valid
 * Called from: WPS validation components, form validation, data integrity checks
 * @param {Object} gridDetail - WPS validation request data
 * Returns: WPS validation results
 */
export async function checkWPSNoPresent(gridDetail) {
  let url = `${WCS_API_URL}/validateWPSinWCS/checkWPSNo`;
  const resp = await axios.post(url, gridDetail)
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return error.message;
    });
  return resp;
}